import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreateDiscount } from "@/hooks/useAdminData";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const discountSchema = z.object({
	code: z
		.string()
		.min(1, "Code is required")
		.regex(/^[A-Z0-9]+$/, "Code must be uppercase letters and numbers only"),
	type: z.enum(["percentage", "flat"]),
	amount: z.coerce.string().refine((val) => Number(val) > 0, "Amount must be greater than 0"),
	max_redemptions: z.coerce.number().min(0).optional(),
	expires_at: z.string().optional(),
	description: z.string().optional(),
});

type DiscountFormValues = z.infer<typeof discountSchema>;

export function AdminDiscountNew() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const mutation = useCreateDiscount();

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<DiscountFormValues>({
		resolver: zodResolver(discountSchema),
		defaultValues: {
			code: "",
			type: "percentage",
			amount: "",
			max_redemptions: undefined,
			expires_at: "",
			description: "",
		},
	});

	function onSubmit(values: DiscountFormValues) {
		const body = {
			code: values.code,
			type: values.type,
			amount: values.amount,
			max_redemptions: values.max_redemptions || undefined,
			expires_at: values.expires_at || undefined,
			description: values.description || undefined,
		};

		mutation.mutate(body, {
			onSuccess: () => {
				toast.success(t("admin.discountCreated"));
				navigate("/admin/discounts");
			},
			onError: () => toast.error(t("admin.saveFailed")),
		});
	}

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[
					{ label: t("admin.dashboard"), href: "/admin" },
					{ label: t("admin.discounts"), href: "/admin/discounts" },
					{ label: t("admin.newDiscount") },
				]}
			/>

			<h1 className="text-3xl font-bold text-primary">{t("admin.newDiscount")}</h1>

			<form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl space-y-6">
				<div className="space-y-2">
					<Label htmlFor="code">{t("admin.code")}</Label>
					<Input id="code" {...register("code")} placeholder="SAVE20" className="uppercase" />
					{errors.code && <p className="text-sm text-error">{errors.code.message}</p>}
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label>{t("admin.type")}</Label>
						<Controller
							control={control}
							name="type"
							render={({ field }) => (
								<Select value={field.value} onValueChange={field.onChange}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="percentage">{t("admin.percentage")}</SelectItem>
										<SelectItem value="flat">{t("admin.flat")}</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="amount">{t("admin.amount")}</Label>
						<Input id="amount" type="number" {...register("amount")} min={0} />
						{errors.amount && <p className="text-sm text-error">{errors.amount.message}</p>}
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label htmlFor="max_redemptions">{t("admin.maxRedemptions")}</Label>
						<Input
							id="max_redemptions"
							type="number"
							{...register("max_redemptions")}
							min={0}
							placeholder={t("admin.unlimited")}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="expires_at">{t("admin.expiryDate")}</Label>
						<Input id="expires_at" type="date" {...register("expires_at")} />
					</div>
				</div>

				<div className="space-y-2">
					<Label htmlFor="description">{t("admin.description")}</Label>
					<Textarea
						id="description"
						{...register("description")}
						placeholder={t("admin.discountDescPlaceholder")}
						rows={3}
					/>
				</div>

				<div className="flex gap-3">
					<Button type="submit" disabled={mutation.isPending}>
						{t("admin.createDiscount")}
					</Button>
					<Button type="button" variant="outline" onClick={() => navigate("/admin/discounts")}>
						{t("admin.cancel")}
					</Button>
				</div>
			</form>
		</div>
	);
}
